import { useEffect, useState } from 'react';
import CodigoQR from '../ui/CodigoQR';
import { PlusCircle, Printer } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { registrarMovimiento } from '../lib/cola';
import { leerConCopia } from '../lib/conCopia';
import { useAuth } from '../lib/contextoAuth';
import NotaGuardadoEnTelefono from '../integrador/pwa/NotaGuardadoEnTelefono';
import { CampoValidado } from '../ui/Formulario';
import { useCambiosSinGuardar } from '../ui/navegacionReact';

const UNIDADES = ['PIEZA', 'KG', 'G', 'L', 'ML', 'M', 'CM', 'CAJA', 'PAQUETE'];

// `irA` llega por el mismo cauce que a cualquier módulo (AppShell se lo pasa
// a todas las pantallas): sirve para enlazar a la ficha del producto y
// completar lo que le falte para venderse.
export default function AltaRapida({ irA }) {
  const { usuario } = useAuth();
  const [f, setF] = useState({ sku: '', nombre: '', unidad_medida: 'PIEZA', cantidad: '', ubicacion: '' });
  const [ubicaciones, setUbicaciones] = useState([]);
  // DIRECTRIZ-PWA F2 (DISENO-F2 §4.5; parche del integrador para
  // experiencia): la hora de la copia si las ubicaciones salieron del
  // teléfono y no de la API. Null = vinieron de la API.
  const [copiaDe, setCopiaDe] = useState(null);
  const [creado, setCreado] = useState(null);
  const [falta, setFalta] = useState(null); // pendientes para venderse/facturarse, o []
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Alta a medias (TC-DR-AT): con algo escrito y sin crear, atrás pregunta.
  useCambiosSinGuardar(() => !creado && [f.sku, f.nombre, f.cantidad].some((v) => String(v).trim() !== ''));

  useEffect(() => {
    leerConCopia(usuario?.id, '/ubicaciones')
      .then(({ datos, momento }) => { setUbicaciones(datos); setCopiaDe(momento); })
      .catch(() => setUbicaciones([]));
  }, [usuario?.id]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function crear(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const prod = await api.post('/productos', {
        sku: f.sku, nombre: f.nombre, unidad_medida: f.unidad_medida,
      });
      // Entrada inicial opcional.
      const cant = Number(f.cantidad);
      if (cant > 0 && f.ubicacion) {
        await registrarMovimiento({
          tipo: 'ENTRADA', sku: prod.sku, cantidad: cant, ubicacion_destino: f.ubicacion,
        });
      }
      setCreado(prod);
      // T-20 · El alta rápida son cuatro campos a propósito, así que el
      // producto nace sin precio ni clave del SAT. En vez de que se
      // descubra al cobrar, se dice AQUÍ qué le falta para venderse y
      // facturarse, con enlace a completarlo. Un fallo no estorba el alta.
      setFalta(null);
      api.get(`/comercial/productos/${encodeURIComponent(prod.sku)}/completitud`)
        .then((c) => setFalta(c.falta))
        .catch(() => setFalta(null));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo dar de alta.');
    } finally {
      setGuardando(false);
    }
  }

  if (creado) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900 pt-2">¡Listo!</h2>
        <p className="text-sm text-gray-600">{creado.nombre}</p>
        <div className="bg-white border border-gray-200 rounded-2xl p-6 inline-flex flex-col items-center gap-3 mx-auto">
          <CodigoQR valor={creado.sku} tamano={200}
                    etiqueta={`Código QR de ${creado.sku}`} />
          <div className="font-mono font-semibold text-gray-800">{creado.sku}</div>
        </div>
        {falta && falta.length > 0 && (
          <div className="text-left bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
            <p className="text-sm font-semibold text-amber-900">
              Para venderlo y facturarlo, le falta:
            </p>
            <ul className="text-sm text-amber-800 list-disc pl-5">
              {falta.map((ff) => <li key={ff.campo}>{ff.etiqueta} — para {ff.para}</li>)}
            </ul>
            {irA && (
              <button type="button" onClick={() => irA('productos', { sku: creado.sku })}
                      className="text-sm font-semibold text-amber-900 underline">
                Completar producto →
              </button>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="flex-1 flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold py-3 rounded-xl"
          >
            <Printer className="h-5 w-5" /> Imprimir etiqueta
          </button>
          <button
            onClick={() => { setCreado(null); setFalta(null); setF({ sku: '', nombre: '', unidad_medida: 'PIEZA', cantidad: '', ubicacion: '' }); }}
            className="flex-1 bg-white border border-gray-200 text-gray-700 font-semibold py-3 rounded-xl"
          >
            Otro
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={crear} className="space-y-3">
      <div className="text-center pt-2">
        <div className="inline-flex bg-[var(--color-marca)] text-white rounded-2xl p-3 mb-1">
          <PlusCircle className="h-7 w-7" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Alta rápida</h2>
      </div>

      {/* Aquí NO se agrupa en secciones: son cinco campos que se rellenan
          de pie y con una mano, y un encabezado por cada dos empuja el botón
          fuera de la pantalla. Lo que sí hace falta es que el error salga
          junto al campo y no al final. */}
      <CampoValidado etiqueta="Nombre" requerido valor={f.nombre}
                     validar={(v) => (v.trim() ? null : 'Ponle nombre.')}>
        {(props) => <input {...props} value={f.nombre} onChange={set('nombre')} />}
      </CampoValidado>

      <CampoValidado etiqueta="SKU / código" requerido valor={f.sku}
                     validar={(v) => {
                       if (!v.trim()) return 'El SKU es obligatorio.';
                       return /\s/.test(v.trim()) ? 'El SKU no lleva espacios.' : null;
                     }}>
        {(props) => <input {...props} value={f.sku} onChange={set('sku')}
                           autoCapitalize="characters" placeholder="Ej. FILT-001" />}
      </CampoValidado>

      <div className="grid grid-cols-2 gap-3">
        <CampoValidado etiqueta="Unidad" valor={f.unidad_medida}>
          {(props) => (
            <select {...props} value={f.unidad_medida} onChange={set('unidad_medida')}>
              {UNIDADES.map((u) => <option key={u}>{u}</option>)}
            </select>
          )}
        </CampoValidado>
        <CampoValidado etiqueta="Cantidad inicial" valor={f.cantidad}
                       validar={(v) => (!String(v).trim() || Number(v) >= 0
                         ? null : 'No puede ser negativa.')}>
          {(props) => <input {...props} type="number" inputMode="decimal" step="any"
                             value={f.cantidad} onChange={set('cantidad')} placeholder="0" />}
        </CampoValidado>
      </div>

      {Number(f.cantidad) > 0 && (
        <CampoValidado etiqueta="Ubicación de entrada" requerido valor={f.ubicacion}
                       ayuda="Si hay cantidad, tiene que entrar en algún sitio."
                       validar={(v) => (v ? null : 'Elige dónde entra.')}>
          {(props) => (
            <select {...props} value={f.ubicacion} onChange={set('ubicacion')} required>
              <option value="">Selecciona…</option>
              {ubicaciones.map((u) => <option key={u.codigo} value={u.codigo}>{u.codigo} — {u.nombre}</option>)}
            </select>
          )}
        </CampoValidado>
      )}
      {Number(f.cantidad) > 0 && <NotaGuardadoEnTelefono momento={copiaDe} que="la lista de ubicaciones" />}

      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}

      <button type="submit" disabled={guardando} className="w-full bg-[var(--color-marca)] disabled:opacity-60 text-white font-bold py-4 rounded-xl">
        {guardando ? 'Guardando…' : 'Crear y generar QR'}
      </button>
    </form>
  );
}

